// Centre slots — the shared loading / load-failure / crash surfaces every
// centre renders inside a pane. Split out of Centre.tsx (hotspot
// decomposition); behaviour unchanged.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../../components/Button/Button.js";
import { ErrorBanner } from "../../../components/ErrorBanner/ErrorBanner.js";
import type { ResourceId } from "../../../components/Navigation/types.js";
import { Spinner } from "../../../components/Spinner/Spinner.js";
import { ErrorBoundary } from "../../ErrorBoundary.js";
import { externalErrorId, localErrorId } from "../../errorId.js";
import type { ExternalError } from "../../externalError.js";
import { toDisplay } from "../../externalError.js";
import { useAppShell } from "../context.js";
import { centeredStyle, paneFallbackActionsStyle } from "../styles.js";

export const spinnerSlot = (label: string): ReactNode => (
    <div style={centeredStyle}>
        <Spinner label={label} />
    </div>
);

// A pane's in-context failure surface (error-handling.md §5): the typed
// display plus its ONE recovery affordance. Retry/Reload re-run the
// pane's load; GoBack closes the failing tab (the resource is gone —
// there is nothing to show); other recoveries render no dead button.
export const PaneError = ({ error }: { error: ExternalError }): ReactNode => {
    const { t } = useLingui();
    const { dispatch, activeRef, mkConversationId, setRetrySeq } =
        useAppShell();
    const d = toDisplay(error);
    const action =
        d.retriable || d.recovery === "Reload" ? (
            <Button
                variant="secondary"
                onClick={() => setRetrySeq((n) => n + 1)}
            >
                {t`Try again`}
            </Button>
        ) : d.recovery === "GoBack" && activeRef !== null ? (
            <Button
                variant="secondary"
                onClick={() =>
                    dispatch({
                        type: "drop-resource-tab",
                        id: activeRef.id,
                        freshConversationId: mkConversationId(),
                    })
                }
            >
                {t`Close tab`}
            </Button>
        ) : undefined;
    return (
        <ErrorBanner
            severity={d.severity}
            title={t(d.title)}
            body={d.body === undefined ? undefined : t(d.body)}
            action={action}
            kept={t`Nothing has been lost — only this view is affected.`}
            errorId={externalErrorId(error)}
        />
    );
};

// A pane's LOCAL crash surface (audit D2): a render throw inside ONE pane's
// body renders HERE instead of white-screening the app. Retry clears the
// crash in place (a transient glitch recovers, no reload); Close drops the
// failing resource tab when the pane's active tab is one. Reuses ErrorBanner,
// the same in-context surface the pane load-failures use.
const PaneFallback = ({
    closeId,
    retry,
}: {
    closeId: ResourceId | null;
    retry: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const { dispatch, mkConversationId } = useAppShell();
    return (
        <div style={centeredStyle}>
            <ErrorBanner
                severity="danger"
                title={t`This view couldn't load`}
                body={t`Something here hit an error.`}
                kept={t`Nothing has been lost — only this view is affected.`}
                errorId={localErrorId("view-crashed")}
                action={
                    <div style={paneFallbackActionsStyle}>
                        <Button variant="secondary" onClick={retry}>
                            {t`Try again`}
                        </Button>
                        {closeId !== null ? (
                            <Button
                                variant="ghost"
                                onClick={() =>
                                    dispatch({
                                        type: "drop-resource-tab",
                                        id: closeId,
                                        freshConversationId: mkConversationId(),
                                    })
                                }
                            >
                                {t`Close`}
                            </Button>
                        ) : null}
                    </div>
                }
            />
        </div>
    );
};

// Wrap a pane's body in its OWN error boundary so a throw fails locally. The
// reset key is the pane's active-tab identity — navigating the pane elsewhere
// clears a stale crash without remounting the healthy body every render.
export const withPaneBoundary = (
    closeId: ResourceId | null,
    resetKey: string,
    body: ReactNode,
): ReactNode => (
    <ErrorBoundary
        fallback={(retry) => <PaneFallback closeId={closeId} retry={retry} />}
        resetKeys={[resetKey]}
    >
        {body}
    </ErrorBoundary>
);
